import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../api/apiClient";
import { IMAGES } from "../constants/theme";
import "./AuthPage.css";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";

const ResetPassword = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";
  const [form, setForm] = useState({ password: "", password_confirmation: "" });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const invalidLink = !token || !email;

  useEffect(() => {
    if (!message) return undefined;
    const redirect = window.setTimeout(() => navigate("/auth", { replace: true }), 2200);
    return () => window.clearTimeout(redirect);
  }, [message, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (form.password !== form.password_confirmation) {
      setError(t("The password confirmation does not match."));
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/reset-password", {
        token,
        email,
        ...form,
      });
      setMessage(data.message);
    } catch (requestError) {
      const errors = requestError.response?.data?.errors;
      setError(
        errors?.password?.[0]
          || errors?.email?.[0]
          || requestError.response?.data?.message
          || t("This reset link is invalid or has expired.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wraper auth-page-wrap">
      <LanguageSwitcher className="hpa-auth-language-switch" />
      <div className="page-content auth-page-content" style={{ backgroundImage: `url(${IMAGES.AuthPerformanceBg})` }}>
        <section className="appointment-page auth-hero">
          <div className="container auth-shell auth-shell-login">
            <div className="auth-card">
              <div className="auth-logo-wrap">
                <img src={IMAGES.logo} alt="Hybrid Performance Academy" className="auth-logo hpa-auth-logo" />
              </div>
              <div className="section-head text-center mb-4">
                <span className="auth-step-label">{t("Step 2 of 2")}</span>
                <h3 className="title mb-2">{t("Choose a new password")}</h3>
                {!invalidLink && <p className="auth-help-text">{t("Resetting the password for {email}.", { email })}</p>}
              </div>

              {invalidLink ? (
                <div className="auth-error" role="alert">
                  {t("This reset link is incomplete. Request a new password reset email.")}
                </div>
              ) : (
                <>
                  {message && <div className="auth-success" role="status">{message} {t("Redirecting to login...")}</div>}
                  {error && <div className="auth-error" role="alert">{error}</div>}
                  {!message && (
                    <form onSubmit={submit} className="auth-form">
                      <div className="form-group mb-3">
                        <label className="auth-field-label" htmlFor="new-password">{t("New password")}</label>
                        <input
                          id="new-password"
                          type="password"
                          className="form-control"
                          value={form.password}
                          onChange={(event) => setForm({ ...form, password: event.target.value })}
                          minLength={8}
                          autoComplete="new-password"
                          required
                        />
                      </div>
                      <div className="form-group mb-3">
                        <label className="auth-field-label" htmlFor="confirm-password">{t("Confirm new password")}</label>
                        <input
                          id="confirm-password"
                          type="password"
                          className="form-control"
                          value={form.password_confirmation}
                          onChange={(event) => setForm({ ...form, password_confirmation: event.target.value })}
                          minLength={8}
                          autoComplete="new-password"
                          required
                        />
                      </div>
                      <button type="submit" className={`btn btn-primary btn-lg btn-skew w-100 auth-submit ${loading ? "auth-submit-loading" : ""}`} disabled={loading}>
                        <span>{loading ? t("Resetting password") : t("Reset password")}</span>
                      </button>
                    </form>
                  )}
                </>
              )}

              <p className="text-center mt-4 mb-0 auth-switch-text">
                <Link to={invalidLink ? "/forgot-password" : "/auth"} className="auth-switch-link">
                  {invalidLink ? t("Request a new link") : t("Back to login")}
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ResetPassword;
